import { useEffect } from "react";
import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { allArticles, allCategories } from "@/lib/articles";
import { applyPageSeo } from "@/lib/seo";

const timeline = [
  { year: "2022", title: "鲁啊鲁启程", text: "从一个小小的内容专栏开始，专注于记录值得被看见的人和事。" },
  { year: "2023", title: "拓展多领域", text: "内容从文化延伸到娱乐与生活，形成三大核心频道。" },
  { year: "2024", title: "内容体系升级", text: "建立分类、标签与专题机制，让阅读更有序、更有深度。" },
  { year: "2025", title: "走向更多读者", text: "持续优化阅读体验与移动端适配，让内容随时随地可读。" },
  { year: "2026", title: "多元内容平台", text: "成为覆盖文化、娱乐、生活的综合资讯平台，服务更广泛的大众用户。" },
];

const team = [
  {
    name: "林清和",
    role: "内容主编",
    bio: "关注文化与社会议题，坚信好内容值得被慢慢读。",
    avatar:
      "https://readdy.ai/api/search-image?query=warm%20friendly%20portrait%20of%20a%20thoughtful%20asian%20editor%20in%20a%20cozy%20office%20with%20soft%20natural%20light%2C%20warm%20beige%20tones%2C%20editorial%20photography%2C%20clean%20minimal%20background%2C%20high%20detail&width=400&height=400&seq=team-01&orientation=squarish",
  },
  {
    name: "苏晚",
    role: "娱乐频道负责人",
    bio: "跑过无数片场与舞台，喜欢把热闹背后的故事讲清楚。",
    avatar:
      "https://readdy.ai/api/search-image?query=warm%20friendly%20portrait%20of%20a%20cheerful%20asian%20woman%20in%20a%20bright%20studio%20with%20soft%20light%2C%20warm%20amber%20tones%2C%20editorial%20photography%2C%20clean%20minimal%20background%2C%20high%20detail&width=400&height=400&seq=team-02&orientation=squarish",
  },
  {
    name: "陈屿",
    role: "生活频道负责人",
    bio: "把日子过明白的实践派，擅长把生活经验写成能用的指南。",
    avatar:
      "https://readdy.ai/api/search-image?query=warm%20friendly%20portrait%20of%20a%20calm%20asian%20man%20in%20a%20sunny%20kitchen%20with%20soft%20light%2C%20warm%20beige%20tones%2C%20editorial%20photography%2C%20clean%20minimal%20background%2C%20high%20detail&width=400&height=400&seq=team-03&orientation=squarish",
  },
  {
    name: "沈知微",
    role: "视觉与体验",
    bio: "相信清晰的排版也是一种尊重，让信息更容易被读懂。",
    avatar:
      "https://readdy.ai/api/search-image?query=warm%20friendly%20portrait%20of%20a%20creative%20asian%20designer%20at%20a%20desk%20with%20soft%20daylight%2C%20warm%20orange%20tones%2C%20editorial%20photography%2C%20clean%20minimal%20background%2C%20high%20detail&width=400&height=400&seq=team-04&orientation=squarish",
  },
];

export default function AboutPage() {
  useEffect(() => {
    applyPageSeo(
      "关于我们 - 鲁啊鲁",
      "了解鲁啊鲁的品牌定位、发展历程与团队故事。我们是一个覆盖文化、娱乐、生活等领域的多元内容资讯平台。",
      "鲁啊鲁,关于我们,团队介绍,品牌定位,内容平台"
    );
  }, []);

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1 w-full">
        <div className="w-full px-4 md:px-8 pt-6">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
        </div>

        <section className="w-full px-4 md:px-8 mt-5">
          <div className="relative overflow-hidden rounded-lg bg-secondary-950">
            <div className="relative h-64 md:h-80 w-full">
              <img
                src="https://readdy.ai/api/search-image?query=warm%20inviting%20editorial%20workspace%20with%20open%20books%20coffee%20and%20a%20soft%20glow%2C%20warm%20orange%20and%20amber%20tones%2C%20artistic%20minimal%20photography%2C%20clean%20abstract%20background%2C%20high%20detail&width=1600&height=600&seq=about-hero-01&orientation=landscape"
                alt="鲁啊鲁品牌介绍"
                className="h-full w-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/55 to-black/30"></div>
              <div className="absolute inset-0 flex flex-col justify-center px-5 md:px-12">
                <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-primary-500/25 px-3 py-1 text-xs font-medium text-primary-200">
                  关于我们
                </span>
                <h1 className="mt-4 font-heading text-2xl font-bold text-white md:text-4xl">
                  鲁啊鲁，让多元内容被温柔以待
                </h1>
                <p className="mt-3 max-w-2xl text-sm text-white/85 md:text-base">
                  我们相信，信息可以不喧闹，阅读可以不匆忙。鲁啊鲁希望做那个陪你把文章读完的地方。
                </p>
              </div>
            </div>
          </div>
        </section>

        <div className="w-full px-4 md:px-8 mt-12 space-y-14 pb-4">
          <section className="grid grid-cols-1 gap-8 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                品牌定位
              </h2>
              <div className="mt-4 space-y-4 text-[15px] leading-relaxed text-foreground-700">
                <p>
                  鲁啊鲁定位为一个内容丰富、覆盖多领域的综合资讯平台。面向关注文化动态、娱乐消息及生活资讯的普通网民，
                  我们力求用简洁易用的界面和多样的内容呈现方式，让用户方便地浏览与筛选，快速获取并深入了解多方面的信息。
                </p>
                <p>
                  我们坚持内容的条理清晰与信息层次，通过分类、标签与专题，把零散的信息组织成有序的知识脉络，
                  让每一次打开都值得，每一次停留都有收获。
                </p>
              </div>
              <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3">
                <div className="rounded-lg bg-background-100 p-4">
                  <p className="font-heading text-2xl font-bold text-primary-600">
                    {allArticles.length}+
                  </p>
                  <p className="mt-1 text-xs text-foreground-600">优质内容</p>
                </div>
                <div className="rounded-lg bg-background-100 p-4">
                  <p className="font-heading text-2xl font-bold text-primary-600">
                    {allCategories.length}
                  </p>
                  <p className="mt-1 text-xs text-foreground-600">核心频道</p>
                </div>
                <div className="rounded-lg bg-background-100 p-4">
                  <p className="font-heading text-2xl font-bold text-primary-600">2022</p>
                  <p className="mt-1 text-xs text-foreground-600">启程之年</p>
                </div>
              </div>
            </div>

            <aside className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h3 className="font-heading text-base font-semibold text-foreground-950">
                我们的频道
              </h3>
              <ul className="mt-4 space-y-3">
                {allCategories.map((category) => (
                  <li key={category.id}>
                    <Link
                      to={`/category/${category.id}`}
                      className="group flex items-center gap-3"
                    >
                      <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-100 text-primary-600 group-hover:bg-primary-500 group-hover:text-background-50 transition-colors">
                        <i className={`${category.icon} text-xl`}></i>
                      </span>
                      <span>
                        <span className="block text-sm font-medium text-foreground-900">
                          {category.name}
                        </span>
                        <span className="block text-xs text-foreground-500">
                          {category.subcategories.map((sub) => sub.name).join(" · ")}
                        </span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </aside>
          </section>

          <section>
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              发展历程
            </h2>
            <p className="mt-1 text-sm text-foreground-600">一路走来，感谢每一次阅读与陪伴</p>
            <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-5">
              {timeline.map((item, index) => (
                <div
                  key={item.year}
                  className="relative rounded-lg border border-background-200 bg-background-50 p-4"
                >
                  <div className="flex items-center gap-2">
                    <span
                      className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold ${
                        index === timeline.length - 1
                          ? "bg-primary-500 text-background-50"
                          : "bg-accent-100 text-accent-800"
                      }`}
                    >
                      {index + 1}
                    </span>
                    <span className="font-heading text-lg font-bold text-primary-600">
                      {item.year}
                    </span>
                  </div>
                  <h3 className="mt-3 font-heading text-sm font-semibold text-foreground-950">
                    {item.title}
                  </h3>
                  <p className="mt-2 text-xs leading-relaxed text-foreground-600">{item.text}</p>
                </div>
              ))}
            </div>
          </section>

          <section>
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              团队介绍
            </h2>
            <p className="mt-1 text-sm text-foreground-600">一群认真对待内容的人</p>
            <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {team.map((member) => (
                <div
                  key={member.name}
                  className="rounded-lg border border-background-200 bg-background-50 p-5 text-center"
                >
                  <div className="mx-auto h-20 w-20 overflow-hidden rounded-full bg-background-200">
                    <img
                      src={member.avatar}
                      alt={member.name}
                      className="h-full w-full object-cover object-top"
                    />
                  </div>
                  <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">
                    {member.name}
                  </h3>
                  <p className="mt-0.5 text-xs font-medium text-primary-600">{member.role}</p>
                  <p className="mt-2 text-xs leading-relaxed text-foreground-600">{member.bio}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="rounded-lg bg-background-100 p-5 md:p-8">
            <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-3">
              <div className="lg:col-span-2">
                <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                  联系我们
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-foreground-700">
                  无论是内容合作、投稿建议还是想法交流，都欢迎随时找到我们。
                </p>
                <ul className="mt-5 space-y-3 text-sm text-foreground-700">
                  <li className="flex items-center gap-2.5">
                    <i className="ri-mail-line w-5 h-5 flex items-center justify-center text-primary-500"></i>
                    合作邮箱：hello@lualu.com
                  </li>
                  <li className="flex items-center gap-2.5">
                    <i className="ri-phone-line w-5 h-5 flex items-center justify-center text-primary-500"></i>
                    联系电话：400-888-2026
                  </li>
                  <li className="flex items-center gap-2.5">
                    <i className="ri-map-pin-line w-5 h-5 flex items-center justify-center text-primary-500"></i>
                    办公地址：山东省济南市历下区文化东路 88 号
                  </li>
                </ul>
                <Link
                  to="/contact"
                  className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  前往联系页面
                  <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                </Link>
              </div>
              <div className="flex flex-col items-center">
                <div className="flex h-40 w-40 items-center justify-center rounded-lg border border-background-300 bg-background-50 text-foreground-400">
                  <i className="ri-qr-code-line text-6xl"></i>
                </div>
                <p className="mt-3 text-sm font-medium text-foreground-800">微信公众号</p>
                <p className="text-xs text-foreground-500">扫码关注，获取最新资讯</p>
              </div>
            </div>
          </section>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}